import React from "react";
import { sanitizeProcessedOrderInfoHtml } from "./sanitizeProcessedOrderInfoHtml";

/**
 * Print-only layout: first row = logo (left) + order QR (right); then metadata, info box, map, footer.
 * Thank-you title/description are screen-only (not printed on the ticket).
 * QR for PDF uses `pdfQrDataUrl` (PNG data URL prepared in the page) when set; falls back to vm image.
 * Hidden on screen via CSS; visible in print preview.
 *
 * @param {object} props
 * @param {object} props.viewModel — from mapProcessedOrderApiToViewModel
 * @param {string | null} [props.pdfQrDataUrl] — PNG data URL after preparation (null = no QR)
 * @param {boolean} [props.pdfQrPreparationDone] — when false, show API image until prep finishes
 */
const ProcessedOrderPrintDocument = React.forwardRef(
  ({ viewModel, pdfQrDataUrl = null, pdfQrPreparationDone = false }, ref) => {
  const v = viewModel;
  const headerSrc = v.printHeaderImageUrl ?? v.customerLogoUrl;
  const bottomSrc = v.printBottomImageUrl ?? v.bottomImageUrl;
  const infoHtml = sanitizeProcessedOrderInfoHtml(v.infoTextBlock);
  const qrSrcForPrint = pdfQrPreparationDone
    ? pdfQrDataUrl
    : v.qrCodeImageSrc ?? null;
  const showTopRow = Boolean(headerSrc || qrSrcForPrint);
  const orderCodeDisplay =
    v.qrCodeValue != null && String(v.qrCodeValue).trim() !== ""
      ? String(v.qrCodeValue).trim()
      : "—";
  const ticketLines = Array.isArray(v.ticketLines) ? v.ticketLines : [];
  const visibleTicketLines = ticketLines.filter((line) => {
    if (!line) return false;
    const q = Number(line.quantity) || 0;
    const t = line.title != null ? String(line.title).trim() : "";
    return q > 0 || (t !== "" && t !== "—");
  });

  return (
    <div className="processed-order-print-root processed-order-print-only">
      {showTopRow ? (
        <header className="processed-order-print-top-row">
          <div className="processed-order-print-top-row-left">
            {headerSrc ? (
              <img className="processed-order-print-logo" src={headerSrc} alt="" />
            ) : null}
          </div>
          <div className="processed-order-print-top-row-right">
            {qrSrcForPrint ? (
              <div className="processed-order-print-qr-wrap">
                <img
                  className="processed-order-print-qr-img"
                  src={qrSrcForPrint}
                  alt="QR code"
                />
              </div>
            ) : null}
          </div>
        </header>
      ) : null}

      <dl className="processed-order-print-meta">
        <dt>Kód objednávky</dt>
        <dd>{orderCodeDisplay}</dd>
        <dt>Dátum vytvorenia</dt>
        <dd>{v.orderCreatedAtDisplay}</dd>
        <dt>Počet lístkov</dt>
        <dd>{v.ticketCount}</dd>
        <dt>Stav</dt>
        <dd>{v.orderStatusLabel}</dd>
      </dl>

      {visibleTicketLines.length > 0 ? (
        <div className="processed-order-print-ticket-lines">
          <div className="processed-order-print-ticket-lines-heading">
            Objednané lístky
          </div>
          <ul className="processed-order-print-ticket-lines-list">
            {visibleTicketLines.map((line, idx) => (
              <li key={idx}>
                <span className="processed-order-print-ticket-lines-qty">
                  {Number(line.quantity) || 0}
                </span>
                <span className="processed-order-print-ticket-lines-sep">×</span>
                <span className="processed-order-print-ticket-lines-title">
                  {line.title != null && String(line.title).trim() !== ""
                    ? String(line.title).trim()
                    : "—"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div
        className="processed-order-print-info-box"
        {...(infoHtml
          ? { dangerouslySetInnerHTML: { __html: infoHtml } }
          : {})}
      />

      {bottomSrc ? (
        <img className="processed-order-print-map" src={bottomSrc} alt="" />
      ) : null}

      {v.footerText ? (
        <footer className="processed-order-print-footer">{v.footerText}</footer>
      ) : null}
    </div>
  );
  },
);

ProcessedOrderPrintDocument.displayName = "ProcessedOrderPrintDocument";

export default ProcessedOrderPrintDocument;
